import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, Users } from "lucide-react";
import { api, type ApiResponse } from "@/api/client";
import { formatDate } from "@ckgo2/shared/utils";

interface NewsletterSubscriber {
  id: string;
  email: string;
  name?: string;
  source: string;
  // Derived server-side (admin/newsletter.ts) from isActive + unsubscribedAt + the consent ledger.
  status: "active" | "unsubscribed" | "suppressed" | "pending";
  createdAt: string;
}

/** Subscribers tab (moved from the retired /newsletter page — block 4.15 řez 3). */
export default function SubscribersPanel() {
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["newsletter-subscribers"],
    queryFn: () =>
      api.get<ApiResponse<NewsletterSubscriber[]>>("/api/admin/newsletter/subscribers"),
  });

  const subscribers = data?.data ?? [];
  // Summary counts in one pass (each status drives only a `.length` in the header).
  const counts = subscribers.reduce<Record<string, number>>((m, s) => {
    m[s.status] = (m[s.status] ?? 0) + 1;
    return m;
  }, {});
  const filtered = subscribers.filter(
    (s) =>
      s.email.toLowerCase().includes(search.toLowerCase()) ||
      (s.name ?? "").toLowerCase().includes(search.toLowerCase()),
  );

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="card space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          <strong>{counts.active ?? 0}</strong> active · {counts.unsubscribed ?? 0} unsubscribed
          {(counts.suppressed ?? 0) > 0 && <> · {counts.suppressed} suppressed</>}
          {(counts.pending ?? 0) > 0 && <> · {counts.pending} pending</>}
        </p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search subscribers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-9"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="flex h-32 flex-col items-center justify-center text-gray-500">
          <Users className="mb-2 h-8 w-8 text-gray-300" />
          <p>No subscribers</p>
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="pb-3 text-left text-sm font-medium text-gray-500">Email</th>
              <th className="pb-3 text-left text-sm font-medium text-gray-500">Name</th>
              <th className="pb-3 text-left text-sm font-medium text-gray-500">Source</th>
              <th className="pb-3 text-left text-sm font-medium text-gray-500">Subscribed</th>
              <th className="pb-3 text-left text-sm font-medium text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((s) => (
              <tr key={s.id}>
                <td className="py-3 text-gray-900">{s.email}</td>
                <td className="py-3 text-gray-600">{s.name || "-"}</td>
                <td className="py-3">
                  <span className="badge text-xs">{s.source}</span>
                </td>
                <td className="py-3 text-sm text-gray-500">{formatDate(s.createdAt)}</td>
                <td className="py-3">
                  {s.status === "active" ? (
                    <span className="badge bg-success-100 text-success-700">Active</span>
                  ) : s.status === "suppressed" ? (
                    <span className="badge bg-amber-100 text-amber-700">Suppressed</span>
                  ) : s.status === "pending" ? (
                    <span className="badge bg-blue-100 text-blue-700">Pending</span>
                  ) : (
                    <span className="badge bg-gray-100 text-gray-600">Unsubscribed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
